import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PersonFinal } from '@/api/entities';
import { useUser } from '@/contexts/UserContext';
import { Upload, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export default function PersonContactInfoUploader() {
  const contextUser = useUser();
  const currentUser = contextUser;
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
      setResults(null);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }

    return data;
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setError(null);
    setResults(null);

    try {
      const text = await file.text();
      const rows = parseCSV(text);

      console.log('📊 Parsed CSV rows:', rows.length);

      if (rows.length === 0) {
        throw new Error('CSV file is empty or invalid');
      }

      // Validate required fields
      const firstRow = rows[0];
      if (!firstRow.person_id) {
        throw new Error('CSV must contain a "person_id" column');
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process in batches to avoid overwhelming the database
      const batchSize = 10;
      for (let i = 0; i < rows.length; i += batchSize) {
        const batch = rows.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (row) => {
          try {
            const personId = row.person_id;
            if (!personId) {
              errorCount++;
              errors.push(`Row ${i + batch.indexOf(row) + 2}: Missing person_id`);
              return;
            }

            // Find the person by person_id or id
            const people = await PersonFinal.filter({});
            const person = people.find(p => 
              p.person_id === personId || p.id === personId
            );

            if (!person) {
              errorCount++;
              errors.push(`Row ${i + batch.indexOf(row) + 2}: Person not found with ID ${personId}`);
              return;
            }

            // Build update object - include fields even if empty to override
            const updateData = {};
            
            if ('email' in row) {
              updateData.email = row.email || null;
            }
            if ('phone' in row) {
              updateData.phone = row.phone || null;
            }

            // Only update if there are fields to update
            if (Object.keys(updateData).length > 0) {
              console.log(`✅ Updating person ${personId}:`, updateData);
              await PersonFinal.update(person.id, updateData);
              successCount++;
            } else {
              errorCount++;
              errors.push(`Row ${i + batch.indexOf(row) + 2}: No contact fields (email, phone) found in CSV`);
            }

          } catch (err) {
            errorCount++;
            errors.push(`Row ${i + batch.indexOf(row) + 2}: ${err.message}`);
            console.error('Error updating person:', err);
          }
        }));

        setProgress(Math.round(((i + batch.length) / rows.length) * 100));
      }

      setResults({
        total: rows.length,
        success: successCount,
        errors: errorCount,
        errorDetails: errors.slice(0, 10) // Show first 10 errors
      });

      console.log('✅ Upload complete:', { successCount, errorCount });

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to process CSV file');
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  if (!contextUser) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Person Contact Info Uploader</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Only show to super admins (role === 'admin')
  if (contextUser.role !== 'admin') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Person Contact Info Uploader</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Access denied. This feature is only available to super administrators.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Person Contact Info Uploader</CardTitle>
        <CardDescription>
          Bulk update email and phone for personas. Upload a CSV with columns: person_id, email, phone.
          <br />
          <strong>Warning:</strong> This will override existing data, even if the CSV field is empty.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#00C2C2] file:text-white hover:file:bg-[#00A3A3]"
              disabled={isUploading}
            />
          </div>
          {file && (
            <p className="text-sm text-gray-600">
              Selected: {file.name}
            </p>
          )}
        </div>

        <Button
          onClick={handleUpload}
          disabled={!file || isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading... {progress}%
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload and Update
            </>
          )}
        </Button>

        {isUploading && (
          <Progress value={progress} className="w-full" />
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {results && (
          <Alert variant={results.errors > 0 ? "default" : "default"} className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-semibold text-green-900">Upload Complete</p>
                <p className="text-sm text-green-800">
                  Total: {results.total} | Success: {results.success} | Errors: {results.errors}
                </p>
                {results.errorDetails && results.errorDetails.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-semibold text-red-900">Errors:</p>
                    <ul className="text-xs text-red-800 list-disc list-inside">
                      {results.errorDetails.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                    {results.errors > 10 && (
                      <p className="text-xs text-red-700 mt-1">
                        ... and {results.errors - 10} more errors
                      </p>
                    )}
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <p className="text-xs font-semibold text-gray-700 mb-2">CSV Format Example:</p>
          <pre className="text-xs text-gray-600 overflow-x-auto">
{`person_id,email,phone
abc123,john@example.com,+1234567890
xyz456,jane@example.com,
def789,,+9876543210`}
          </pre>
          <p className="text-xs text-gray-600 mt-2">
            • person_id is required (must match existing person records)
            <br />
            • Empty values will clear the existing data
            <br />
            • Include only the columns you want to update
          </p>
        </div>
      </CardContent>
    </Card>
  );
}